/**
 * Mẫu hợp đồng chuẩn Minh Tiến (anh Tiến gửi 09/10/2026) – dạng chữ có đánh dấu, dùng chung cho xem trước (web) và in Word / PDF (API).
 *
 * Mỗi dòng là một đoạn văn:
 * - `# Tiêu đề`, `## Tiêu đề phụ` (in đậm); `^ chữ` căn giữa; `^^ TÊN HỢP ĐỒNG` tên hợp đồng (căn giữa, chữ lớn); còn lại căn đều hai bên.
 * - Trong dòng: `**đậm**`, `__nghiêng__`.
 * - Bảng: dòng `::table widths=8,42,22,28 border=all|none` rồi các dòng `| ô | ô |`; trong ô `<br>` xuống dòng, `^` đầu dòng căn giữa,
 *   `{cs=N}` đầu ô gộp N cột.
 * - `[BẢNG HÀNG HÓA]` (riêng một dòng): bảng hàng hóa của đơn / báo giá. `[CHỮ KÝ KHÁCH]` (trong ô ký): chữ ký khách / người lao động ký online hoặc chỗ trống để ký;
 *   `[CHỮ KÝ CÔNG TY]`: chữ ký người đại diện công ty ký trên ứng dụng (hợp đồng nhân sự, P21 PR 9).
 * - Ô trộn `{Tên}` hoặc `{Tên|chỗ trống gốc}`: có dữ liệu thì in dữ liệu, không có thì in chỗ trống gốc của mẫu (mặc định dòng chấm).
 */
import type { ContractArticle } from './contract';

export interface DocRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
}
export type DocAlign = 'left' | 'center' | 'justify';
export interface DocLine {
  runs: DocRun[];
  align: DocAlign;
  /** Dòng `[CHỮ KÝ KHÁCH]` trong ô ký */
  signature?: boolean;
  /** Dòng `[CHỮ KÝ CÔNG TY]` trong ô ký */
  companySignature?: boolean;
}
export interface DocCell {
  lines: DocLine[];
  span: number;
}
export type DocBlock =
  | { kind: 'p'; runs: DocRun[]; align: DocAlign; level: 0 | 1 | 2; title?: boolean }
  | { kind: 'table'; widths: number[]; border: boolean; rows: DocCell[][] }
  | { kind: 'items' };

export const DOC_ITEMS_MARK = '[BẢNG HÀNG HÓA]';
export const DOC_SIGNATURE_MARK = '[CHỮ KÝ KHÁCH]';
export const DOC_COMPANY_SIGNATURE_MARK = '[CHỮ KÝ CÔNG TY]';
export const DOC_BLANK = '……………………';

/** Mẫu hợp đồng bán hàng chuẩn: phần đầu + điều khoản (sửa được trên từng hợp đồng) + phần ký; `internal` = checklist nội bộ, không in */
export interface StdContractTemplate {
  type: 'std_economic' | 'std_supply';
  headerText?: string;
  footerText: string;
  /** Bên A / Bên B là bên nào trong mẫu */
  partyLetters: { buyer: 'A' | 'B'; seller: 'A' | 'B' };
  /** Căn cứ pháp lý gốc của mẫu (giá trị mặc định của trường "Căn cứ pháp lý") */
  basis: string;
  head: string;
  articles: ContractArticle[];
  tail: string;
  internal: string;
}

const PLACEHOLDER = /\{([^{}|\n]{1,60})(?:\|([^{}\n]{0,400}))?\}/g;

/**
 * Thay ô trộn. resolve(tên) → chuỗi (rỗng = chưa có dữ liệu → in chỗ trống gốc) hoặc null (không phải ô trộn → giữ nguyên chữ).
 * Giá trị nhiều dòng: trong bảng nối bằng `<br>`, ngoài bảng tách thành nhiều đoạn. Ký tự đánh dấu trong giá trị được vô hiệu hóa.
 */
export function fillDocPlaceholders(src: string, resolve: (name: string) => string | null): string {
  return src
    .split('\n')
    .map((line) => {
      const inTable = line.trimStart().startsWith('|');
      return line.replace(PLACEHOLDER, (all, rawName: string, fallback: string | undefined) => {
        const v = resolve(rawName.trim());
        if (v === null) return all;
        let out = v.trim() ? v.trim() : fallback !== undefined ? fallback : DOC_BLANK;
        out = out.replace(/\*\*/g, '*​*').replace(/__/g, '_​_').replace(/\{/g, '(').replace(/\}/g, ')');
        if (inTable) out = out.replace(/\|/g, '/').replace(/\r?\n+/g, '<br>');
        else out = out.replace(/\r?\n+/g, '\n');
        return out;
      });
    })
    .join('\n');
}

/** Tên các ô trộn có trong đoạn chữ (không trùng, theo thứ tự xuất hiện) */
export function docPlaceholderNames(src: string): string[] {
  const out: string[] = [];
  for (const m of src.matchAll(PLACEHOLDER)) {
    const n = m[1]!.trim();
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** `**đậm**`, `__nghiêng__` → các đoạn chữ */
export function parseDocRuns(text: string): DocRun[] {
  const runs: DocRun[] = [];
  let bold = false;
  let italic = false;
  let buf = '';
  const flush = () => {
    if (buf) runs.push({ text: buf, ...(bold ? { bold: true } : {}), ...(italic ? { italic: true } : {}) });
    buf = '';
  };
  for (let i = 0; i < text.length; i++) {
    const two = text.slice(i, i + 2);
    if (two === '**') {
      flush();
      bold = !bold;
      i++;
    } else if (two === '__') {
      flush();
      italic = !italic;
      i++;
    } else buf += text[i];
  }
  flush();
  return runs;
}

function parseCell(raw: string): DocCell {
  let text = raw.trim();
  let span = 1;
  const m = /^\{cs=(\d+)\}/.exec(text);
  if (m) {
    span = Math.max(1, Number(m[1]));
    text = text.slice(m[0].length);
  }
  const lines = text.split('<br>').map((l): DocLine => {
    let t = l.trim();
    let align: DocAlign = 'left';
    if (t.startsWith('^')) {
      align = 'center';
      t = t.slice(1).trim();
    }
    if (t === DOC_SIGNATURE_MARK) return { runs: [], align: 'center', signature: true };
    if (t === DOC_COMPANY_SIGNATURE_MARK) return { runs: [], align: 'center', companySignature: true };
    return { runs: parseDocRuns(t), align };
  });
  return { lines, span };
}

/** Đoạn chữ đã thay ô trộn → các khối in (đoạn văn, bảng, bảng hàng hóa) */
export function parseDocMarkup(src: string): DocBlock[] {
  const blocks: DocBlock[] = [];
  const lines = src.split('\n');
  let tableOpts: { widths: number[]; border: boolean } | null = null;
  let table: Extract<DocBlock, { kind: 'table' }> | null = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('|')) {
      if (!table) {
        table = { kind: 'table', widths: tableOpts?.widths ?? [], border: tableOpts?.border ?? true, rows: [] };
        blocks.push(table);
        tableOpts = null;
      }
      const inner = line.replace(/^\|/, '').replace(/\|$/, '');
      table.rows.push(inner.split('|').map(parseCell));
      continue;
    }
    if (table) {
      if (!table.widths.length) {
        const n = Math.max(...table.rows.map((r) => r.reduce((s, c) => s + c.span, 0)));
        table.widths = Array.from({ length: n }, () => 100 / n);
      }
      table = null;
    }
    if (!line) continue;
    const opt = /^::table\s+(.*)$/.exec(line);
    if (opt) {
      const w = /widths=([\d.,]+)/.exec(opt[1]!);
      tableOpts = { widths: w ? w[1]!.split(',').map(Number) : [], border: !/border=none/.test(opt[1]!) };
      continue;
    }
    if (line === DOC_ITEMS_MARK) {
      blocks.push({ kind: 'items' });
      continue;
    }
    if (line.startsWith('^^ ')) blocks.push({ kind: 'p', runs: parseDocRuns(line.slice(3)), align: 'center', level: 0, title: true });
    else if (line.startsWith('^ ')) blocks.push({ kind: 'p', runs: parseDocRuns(line.slice(2)), align: 'center', level: 0 });
    else if (line.startsWith('## ')) blocks.push({ kind: 'p', runs: parseDocRuns(line.slice(3)), align: 'left', level: 2 });
    else if (line.startsWith('# ')) blocks.push({ kind: 'p', runs: parseDocRuns(line.slice(2)), align: 'left', level: 1 });
    else blocks.push({ kind: 'p', runs: parseDocRuns(line), align: 'justify', level: 0 });
  }
  if (table && !table.widths.length) {
    const n = Math.max(...table.rows.map((r) => r.reduce((s, c) => s + c.span, 0)));
    table.widths = Array.from({ length: n }, () => 100 / n);
  }
  return blocks;
}

/** Bỏ đánh dấu → chữ thường (tìm kiếm, kiểm thử, checklist) */
export function docPlainText(src: string): string {
  return src
    .split('\n')
    .filter((l) => !l.trim().startsWith('::'))
    .map((l) => l.replace(/^\s*(\^\^|\^|##|#)\s+/, '').replace(/\*\*|__/g, '').replace(/<br>/g, ' / '))
    .join('\n');
}
